import { ViewChild, ElementRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { CourseCatalogPostingStream } from 'app/library/course-catalog-landing-page/models/course-catalog-posting-stream.model';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CourseCatalogService } from '../../services/course-catalog.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-course-catalog-search',
    templateUrl: './course-catalog-search.component.html',
    styleUrls: ['./course-catalog-search.component.scss'],
})
export class CourseCatalogSearchComponent implements OnInit {
    @ViewChild('searchInputElement') searchInputElement!: ElementRef<HTMLInputElement>;

    @Output()
    searchEvent = new EventEmitter<string>();

    @Output()
    enterPressedEvent = new EventEmitter<string>();

    @Output()
    currentSearchValue = new EventEmitter<string>();

    @Output()
    isFocusedEvent = new EventEmitter<boolean>();

    @Input()
    searchTerm: string;

    @Input()
    recentlyViewedPostings: CourseCatalogPostingStream[] = [];

    @Input()
    suggestedSearchPostings: CourseCatalogPostingStream[] = [];

    @Input()
    isExpanded: boolean = true;

    isDropdownVisible = false;
    searchInput = new FormControl('');
    lastSearches: string[];

    constructor(private _courseCatalogService: CourseCatalogService, private _router: Router) {}

    async ngOnInit(): Promise<void> {
        this.lastSearches = JSON.parse(localStorage.getItem('courseCatalogSearchHistory')) || [];

        if (this.searchTerm) {
            this.searchInput.setValue(this.searchTerm);
        }

        this.searchInput.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((value) => {
            const trimmedValue = value?.trim() || '';
            this.searchEvent.emit(trimmedValue);
        });
    }

    onFocus(): void {
        if (this.isExpanded) {
            this.isFocusedEvent.emit(true);
            this.isDropdownVisible = true;
        }
    }

    onBlur(): void {
        setTimeout(() => {
            // call search method before hidding dropdown (300ms > 400ms)
            this.searchInputElement.nativeElement.blur();
            this.isDropdownVisible = false;
            this.isFocusedEvent.emit(false);
        }, 400);
    }

    removeLastSearch(searchTerm: string, event?: MouseEvent) {
        if (event) {
            event.preventDefault(); // prevent input blur
            event.stopPropagation();
        }

        const index = this.lastSearches.indexOf(searchTerm);
        if (index > -1) {
            this.lastSearches.splice(index, 1);
            localStorage.setItem('courseCatalogSearchHistory', JSON.stringify(this.lastSearches));
        }

        // Keep input focused
        this.searchInputElement.nativeElement.focus();
    }

    search(searchInputValue: string): void {
        const trimmedValue = searchInputValue.trim();

        this.lastSearches = JSON.parse(localStorage.getItem('courseCatalogSearchHistory')) || [];

        const existingIndex = this.lastSearches.indexOf(trimmedValue);
        if (existingIndex > -1) {
            this.lastSearches.splice(existingIndex, 1);
        }

        if (trimmedValue && this.isExpanded) {
            this.lastSearches.unshift(trimmedValue);

            if (this.lastSearches.length > 5) {
                this.lastSearches.pop();
            }

            localStorage.setItem('courseCatalogSearchHistory', JSON.stringify(this.lastSearches));
        }

        this.searchInput.setValue(trimmedValue);
        this.currentSearchValue.emit(this.searchInput.value);
        this.searchEvent.emit(trimmedValue);
        this.enterPressedEvent.emit(trimmedValue);
        this.onBlur();
    }

    highlightMatch(text: string, searchTerm: string): string {
        if (!searchTerm) return text;

        const regex = new RegExp(`(${searchTerm})`, 'gi');
        return text.replace(regex, '<strong>$1</strong>');
    }

    navigateTo(postingResource: CourseCatalogPostingStream): void {
        // If the url starts with the same domain as the custom domain (whole one, not just base domain) then open internally
        if (postingResource.url.includes(window.location.host)) {
            const url = new URL(postingResource.url);
            const segments = url.pathname.split('/').filter(Boolean); // npr. ['catalog', '123']
            const categoryId = segments[segments.length - 1]; // uzmi zadnji segment

            // Open sidebar category view when clicking on category posting card
            if (postingResource.kind === 'course_category') {
                this._courseCatalogService.setSelectedCategoryId(categoryId);
            }

            this._router.navigateByUrl(url.pathname);
        } else {
            window.open(postingResource.url, '_blank');
        }
    }
}
